import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";
import BreadCrumb from "Common/BreadCrumb";
import { PaginationState } from "@tanstack/react-table";
import TableContainer from "Common/TableContainer";
import { Link, useNavigate } from "react-router-dom";
import {
  MoreHorizontal,
  Eye,
  FileEdit,
  Trash2,
  UploadCloud,
} from "lucide-react";
import { Dropdown } from "Common/Components/Dropdown";
import { Download, Plus, Search, SlidersHorizontal } from "lucide-react";
import HTMLReactParser from "html-react-parser";
import DeleteModal from "Common/DeleteModal";
import callApi from "Common/utils/apiCaller";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import _ from "lodash";
import Modal from "Common/Components/Modal";
import { createSelector } from "reselect";
import { useSelector, useDispatch } from "react-redux";
import { getImageUrl } from "Common/utils/ImageDomain";
import PermissionSwitch from "../../Common/Components/PermissionLocks/PermissionSwitch";
import { CONTENT_ADD_ROUTE, CONTENT_UPDATE_ROUTE } from "constants/routeEnum";

const Content = () => {
  const navigate = useNavigate();
  const [content, setcontent] = useState([]);
  const [search, setSearch] = useState<any>();
  const [showModel, setShowModel] = useState<boolean>(false);
  const [read, setRead] = useState<any>();
  const defaultToggle = () => setShowModel(!showModel);
  const [imageLink, setImageLink] = useState<any>("");
  const [pageCount, setPageCount] = useState(0);
  const [eventData, setEventData] = useState<any>();
  const [totalItems, setTotalItems] = useState(0);
  const [render, setRender] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 30,
  });
  const [deleteModal, setDeleteModal] = useState<boolean>(false);

  const selectProperties = createSelector(
    (state: any) => state.Login,
    (profile) => ({
      permission: profile.permission,
      user: profile.user,
    })
  );

  const { permission, user } = useSelector(selectProperties);

  const deleteToggle = () => setDeleteModal(!deleteModal);
  const debouncedHandler = useCallback(
    _.debounce((user: any, render: any, pagination: any) => {
      return fetchDateWithFilters(user, render, pagination);
    }, 700),
    []
  );

  async function fetchDateWithFilters(
    searchByName: any,
    render: any,
    pagination: any
  ) {
    let filters: { [index: string]: any } = {};
    if (searchByName) {
      filters.qtext = searchByName;
    }

    const localData = await callApi("v1/content/list", "post", {
      pageNum: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      filters,
    });
    let arr = [];
    if (localData?.status === "Success") {
      setcontent([...localData.data.contentList]);
      setPageCount(
        Math.ceil(localData.data.contentCount / pagination.pageSize)
      );
      setTotalItems(localData.data.contentCount);
    }
  }

  const filterSearchData = (e: any) => {
    const search = e.target.value;

    setSearch(search);
  };

  useEffect(() => {
    debouncedHandler(search, render, pagination);
  }, [search, render, pagination]);

  // Delete Data
  const onClickDelete = (cell: any) => {
    setDeleteModal(true);
    if (cell._id) {
      setEventData(cell);
    }
  };

  const handleDelete = async () => {
    if (eventData) {
      //dispatch(onDeleteEmployee(eventData.id));

      const res = await callApi(`v1/content/${eventData?._id}/update`, "post", {
        content: { is_deleted: true },
      });
      if (res && res?.status === "Success") {
        setDeleteModal(false);
        setRender(!render);
        toast("Deleted successfully");
      }
    }
  };

  const contentColumns = useMemo(
    () => [
      {
        header: "Code",
        accessorKey: "code",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Edit",
        accessorKey: "edit",
        cell: ({ cell }: any) => (
          <PermissionSwitch
            hasPermissions={permission.permissions}
            shouldHavePermissions={["admin:edit"]}
            yes={
              <button
                className="text-blue-500 hover:text-blue-700"
                onClick={() => {
                  const data = cell.row.original;
                  navigate(`${CONTENT_UPDATE_ROUTE.replace(":id", data._id)}`);
                }}
              >
                Edit
              </button>
            }
            no={<span></span>}
          />
        ),
      },
      {
        header: "Delete ",
        accessorKey: "delete",
        cell: ({ cell }: any) => (
          <PermissionSwitch
            hasPermissions={permission.permissions}
            shouldHavePermissions={["admin:delete"]}
            yes={
              <button
                className="text-red-500 hover:text-red-700"
                onClick={() => {
                  const data = cell.row.original;
                  onClickDelete(data);
                }}
              >
                Delete
              </button>
            }
            no={<span></span>}
          />
        ),
      },
    ],
    []
  );

  return (
    <>
      <React.Fragment>
        <BreadCrumb title="content List" pageTitle="content" version="" />
        <DeleteModal
          show={deleteModal}
          onHide={deleteToggle}
          onDelete={handleDelete}
        />
        <ToastContainer closeButton={false} limit={1} />

        <div className="grid grid-cols-12 gap-x-5">
          <div className="col-span-12 card 2xl:col-span-12">
            <div className="card-body">
              <div className="flex items-center">
                <h6 className="text-15 grow">content</h6>
                <div className="shrink-0">
                  <PermissionSwitch
                    hasPermissions={permission.permissions}
                    shouldHavePermissions={["admin:create"]}
                    yes={
                      <button
                        type="button"
                        className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                        onClick={() => {
                          navigate(CONTENT_ADD_ROUTE);
                        }}
                      >
                        <Plus className="inline-block size-4" />{" "}
                        <span className="align-middle">Add content</span>
                      </button>
                    }
                    no={
                      <button
                        type="button"
                        disabled={true}
                        className="text-grey btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                        onClick={null}
                      >
                        <Plus className="inline-block size-4" />{" "}
                        <span className="align-middle">Add content</span>
                      </button>
                    }
                  />
                </div>
              </div>
            </div>
            <div className="!py-3.5 card-body border-y border-dashed border-slate-200 dark:border-zink-500">
              <form action="#!">
                <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
                  <div className="relative xl:col-span-3">
                    <input
                      type="text"
                      className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Search for code..."
                      autoComplete="off"
                      onChange={(e) => filterSearchData(e)}
                    />
                    <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                  </div>
                </div>
              </form>
            </div>
            <div className="card-body">
              <TableContainer
                isPagination={true}
                columns={contentColumns || []}
                data={content || []}
                pageCount={pageCount}
                pagination={pagination}
                totalItems={totalItems}
                onPaginationChange={(updater: any) => {
                  const nextState = updater(pagination);
                  if (nextState.pageIndex !== pagination.pageIndex)
                    setPagination(nextState);
                }}
                divclassName="overflow-x-auto"
                tableclassName="w-full whitespace-nowrap"
                theadclassName="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:text-zink-200 dark:bg-zink-600"
                thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-y border-slate-200 dark:border-zink-500"
                tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500"
                PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
              />
            </div>
          </div>
        </div>

        <Modal
          show={showModel}
          onHide={defaultToggle}
          id="defaultModal"
          modal-center="true"
          className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
          dialogClassName="w-screen md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600 flex flex-col h-full"
        >
          <Modal.Header
            className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500"
            closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500 dark:text-zink-200 dark:hover:text-red-500"
          >
            <Modal.Title className="text-16">{"Text"}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
            {/* <h5 className="mb-3 text-16">{}</h5> */}
            <p className="text-slate-500 dark:text-zink-200">
              {HTMLReactParser(read ?? "")}
            </p>
          </Modal.Body>
        </Modal>
      </React.Fragment>
    </>
  );
};

export default Content;
